import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import FormWarrantyClaims from './component/FormWarrantyClaims';
import StatusChangeModal from './component/StatusChangeModal';
import useWarrantyClaimsMutations from '../hook/useWarrantyClaimsMutations';
import { useGetWarrantyClaimsByIdQuery } from '../../../../../api';
import { useChangeRejectedOrAcceptedStatusMutation } from '../../../../../api/endpoints/warrantyClaims';
import WarrantyClaimsDetailTabs from './warrantyClaimsDetailTabs/WarrantyClaimsDetailTabs';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useGetDeclarationByIdQuery } from '../../../../../api/customerDeclarationApi';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { WarrantyClaimStatus } from '../../../../../utils/warrantyClaimsEnums';
import { usePermissions } from '../../../../../hooks/usePermissions';
import toastService from '../../../../../utils/toastService';

const WarrantyClaimsCreateEdit = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const { hasAdminRole, hasWarrantyManagerRole } = usePermissions();

    const customerDeclarationData = location.state?.customerDeclarationData;

    const initialState = {
        chassisNo: '',
        warrantyType: '',
        subWarrantyType: 0,
        failureDate: '',
        repairKm: 0,
        claimDate: '',
        kmInformation: 0,
        bulletNo: '',
        bosBranchCode: '1',
        bosBranchSiteCode: '1',
        dealerComment: '',
        causeOfailure: '',
        failureSolution: '',
        roadAssCost: 0,
        totalPrice: 0,
        mainCategory: 0,
        secondCategory: 0,
        subCategory: 0,
        subPartCategory: 0,
        failureMode: 0,
        forEvaluation: '',
        alokasyon: 0,
        alokasyonName: '',
        customerStatementId: '',
        tcNo: '',
        dealerPackageIds: [],
        dealerPackageName: '',
        packageWarrantyType: null,
        preAppNo: '',
        claimBranchDealerName: '',
        plateNo: ''
    };

    const { data, isSuccess, error: getByIdError, refetch: refetchClaimById } = useGetWarrantyClaimsByIdQuery({ id, languageId: getCurrentLanguageId() }, { skip: !isEditMode });

    const [formData, setFormData] = useState(initialState);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [statusChangeModalVisible, setStatusChangeModalVisible] = useState(false);

    const [changeRejectedOrAcceptedStatus, { isLoading: isChangingStatus }] = useChangeRejectedOrAcceptedStatusMutation();

    const customerStatementId = data?.customerStatementId || customerDeclarationData?.id;

    const { data: declarationResponse, isSuccess: isDeclarationSuccess } = useGetDeclarationByIdQuery(customerStatementId, { skip: !customerStatementId });

    const { saveWarrantyClaims, handleActionSave, isCreating, isUpdating, isActionLoading, isRedirecting } = useWarrantyClaimsMutations();

    useEffect(() => {
        if (getByIdError) {
            const httpStatus = getByIdError.status === 'PARSING_ERROR' ? getByIdError.originalStatus : getByIdError.status;
            if (httpStatus === 403) {
                toastService.warn(th('common.noRecordPermission'), th('common.warning'), 6000);
                navigate('/warranty-management/warranty-requests/claims');
            }
        }
    }, [getByIdError, navigate, th]);

    useEffect(() => {
        if (!isEditMode && customerDeclarationData) {
            const locationState = location.state || {};
            setFormData((prev) => ({
                ...prev,
                chassisNo: customerDeclarationData.chassisNumber || '',
                customerStatementId: locationState.customerStatementId || customerDeclarationData.id || '',
                claimDate: locationState.claimDate || '',
                kmInformation: locationState.kmInformation || 0
            }));
        }
    }, [customerDeclarationData, isEditMode, location.state]);

    useEffect(() => {
        if (isEditMode && isSuccess && data) {
            setFormData({
                chassisNo: data.chassisNo || '',
                warrantyType: data.warrantyType || data.warrType || '',
                subWarrantyType: data.subWarrantyType ? data.subWarrantyType : 0,
                failureDate: data.failureDate ? new Date(data.failureDate) : '',
                repairKm: data.repairKm || 0,
                claimDate: data.claimDate ? new Date(data.claimDate) : '',
                kmInformation: data.kmInformation || 0,
                bulletNo: data.bulletNo || '',
                bosBranchCode: data.bosBranchCode || '1',
                bosBranchSiteCode: data.bosBranchSiteCode || '1',
                dealerComment: data.dealerComment || '',
                causeOfailure: data.causeOfailure || '',
                failureSolution: data.failureSolution || '',
                roadAssCost: data.roadAssCost || 0,
                totalPrice: data.totalPrice || 0,
                mainCategory: data?.mainCategory || 0,
                secondCategory: data?.secondaryCategory || 0,
                subCategory: data?.subCategory || 0,
                subPartCategory: data?.subPartCategory || 0,
                mainCategoryName: data?.mainCategoryName || '-',
                secondCategoryName: data?.secondaryCategoryName || '-',
                subCategoryName: data?.subCategoryName || '-',
                subPartCategoryName: data?.subPartCategoryName || '-',
                failureMode: data?.failureMode || 0,
                failureModeName: data?.failureModeName || '-',
                forEvaluation: data.forEvaluation || '',
                alokasyon: data.alokasyon || 0,
                alokasyonName: data.alokasyonName || 0,
                tcNo: data.tcNo || '',
                dealerPackageIds: data.dealerPackageIds || [],
                dealerPackageName: data.dealerPackageName || '-',
                packageWarrantyType: data.packageWarrantyType || null,
                packageWarrantyTypeName: data.dealerPackageWarrantyTypeName || '-',
                preAppNo: data.preAppNo || '',
                claimBranchDealerName: data.claimBranchDealerName || '',
                plateNo: data.plateNo || ''
            });
        }
    }, [isEditMode, isSuccess, data]);

    useEffect(() => {
        if (isDeclarationSuccess && declarationResponse?.customerStatement) {
            const shouldUpdate = (!isEditMode || data?.status === WarrantyClaimStatus.DRAFT) && !formData.dealerComment;

            if (shouldUpdate) {
                setFormData((prev) => ({
                    ...prev,
                    dealerComment: declarationResponse.customerStatement
                }));
            }
        }
    }, [isDeclarationSuccess, declarationResponse, isEditMode, data?.status]);

    const validateTcNo = (tcNo) => {
        const tcNoRegex = /^\d{11}$/;
        return tcNoRegex.test(tcNo);
    };

    const handleChange = useCallback(
        (key, value) => {
            const categoryKeys = ['mainCategory', 'secondCategory', 'subCategory', 'subPartCategory'];

            let processedValue = value;

            if (categoryKeys.includes(key) && typeof value === 'string') {
                const match = value.match(/^\d+/);
                if (match) {
                    processedValue = parseInt(match[0], 10);
                }
            }

            if (key === 'tcNo') {
                const newErrors = { ...errors };

                if (value && !validateTcNo(value)) {
                    newErrors.tcNo = 'TC kimlik numarası 11 haneli olmalıdır';
                } else {
                    delete newErrors.tcNo;
                }

                setErrors(newErrors);
            }

            setFormData((prev) => ({
                ...prev,
                [key]: processedValue
            }));
        },
        [errors]
    );

    const canChangeStatus = isEditMode && data && (data?.status === WarrantyClaimStatus.REJECTED || data?.status === WarrantyClaimStatus.ACCEPTED) && (hasAdminRole() || hasWarrantyManagerRole());

    // Faturası kesilmiş bir talebin statüsü artık değiştirilemez
    const isInvoiceIssued = Boolean(data?.hasInvoice);

    const currentStatusSeverity = data?.status === WarrantyClaimStatus.REJECTED ? 'danger' : data?.status === WarrantyClaimStatus.ACCEPTED ? 'success' : 'info';

    const getLocalizedStatusName = (statusCode) => {
        switch (statusCode) {
            case WarrantyClaimStatus.DRAFT:
                return th('warrantyClaims.tabs.drafts');
            case WarrantyClaimStatus.WAITING:
                return th('warrantyClaims.tabs.waiting');
            case WarrantyClaimStatus.RETURNED:
                return th('warrantyClaims.tabs.returned');
            case WarrantyClaimStatus.RETURNED_TO_KARSAN:
                return th('warrantyClaims.tabs.returnedToKarsan');
            case WarrantyClaimStatus.ACCEPTED:
                return th('warrantyClaims.tabs.accepted');
            case WarrantyClaimStatus.REJECTED:
                return th('warrantyClaims.tabs.rejected');
            default:
                return '-';
        }
    };

    const localizedCurrentStatusName = getLocalizedStatusName(data?.status);

    const mapStatusChangeError = (error) => {
        const httpStatus = error?.status === 'PARSING_ERROR' ? error.originalStatus : error?.status;
        const message = error?.data?.message || error?.data?.errorMessage || error?.message || '';

        if (typeof message === 'string') {
            if (message.includes('CANNOT_CHANGE_STATUS_GT_INVOICE_EXISTS')) {
                return th('warrantyClaims.statusChange.errors.gtInvoiceExists');
            }
            if (message.includes('INVALID_STATUS')) {
                return th('warrantyClaims.statusChange.errors.invalidStatus');
            }
            if (message.includes('WORKFLOW_STATE_INVALID')) {
                return th('warrantyClaims.statusChange.errors.workflowStateInvalid');
            }
        }

        if (httpStatus === 403) {
            return th('warrantyClaims.statusChange.errors.forbidden');
        }
        if (httpStatus === 404) {
            return th('warrantyClaims.statusChange.errors.notFound');
        }

        return th('warrantyClaims.statusChange.errors.generic');
    };

    const handleStatusChangeConfirm = async (newStatus, reason) => {
        try {
            await changeRejectedOrAcceptedStatus({
                claimId: Number(id),
                newStatus: Number(newStatus),
                reason: reason || ''
            }).unwrap();

            toastService.success(th('warrantyClaims.statusChange.successMessage'), th('common.success'));
            setStatusChangeModalVisible(false);

            if (typeof refetchClaimById === 'function') {
                try {
                    refetchClaimById();
                } catch (e) {
                    // ignore refetch errors - cache invalidation will handle most cases
                }
            }

            navigate('/warranty-management/warranty-requests/claims');
        } catch (error) {
            console.error('Error changing warranty claim status:', error);
            const errorMessage = mapStatusChangeError(error);
            toastService.error(errorMessage, th('common.error'), 5000);
        }
    };

    return (
        <div className="card">
            {isRedirecting && (
                <div
                    className="surface-0 flex align-items-center justify-content-center"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }}
                >
                    <div className="text-center">
                        <ProgressSpinner style={{ width: '60px', height: '60px' }} strokeWidth="3" fill="var(--surface-ground)" animationDuration=".75s" />
                        <div className="mt-3 text-600 font-medium">{th('warrantyClaims.messages.redirecting')}</div>
                    </div>
                </div>
            )}
            <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                <h2 className="m-0">{isEditMode ? th('warrantyClaims.detail.title') : th('warrantyClaims.detail.createTitle')}</h2>
                {canChangeStatus && (
                    <div className="flex align-items-center gap-2">
                        <span className="font-bold">{th('warrantyClaims.statusChange.currentStatus')}:</span>
                        <Tag value={localizedCurrentStatusName} severity={currentStatusSeverity} />
                        <Button label={th('warrantyClaims.statusChange.button')} icon="pi pi-refresh" severity="info" onClick={() => setStatusChangeModalVisible(true)} disabled={isChangingStatus || isInvoiceIssued} />
                        {isInvoiceIssued && <i className="pi pi-lock text-500" title={th('warrantyClaims.statusChange.errors.gtInvoiceExists')}></i>}
                    </div>
                )}
            </div>
            <FormWarrantyClaims
                id={id}
                data={data}
                editMode={isEditMode}
                submitted={submitted}
                formData={formData}
                handleChange={handleChange}
                saveWarrantyClaims={saveWarrantyClaims}
                handleActionSave={handleActionSave}
                resetForm={() => {
                    setFormData(initialState);
                    setSubmitted(false);
                    setErrors({});
                }}
                setSubmitted={setSubmitted}
                errors={errors}
                isCreating={isCreating}
                isUpdating={isUpdating}
                isActionLoading={isActionLoading}
                hasCustomerStatement={!!declarationResponse?.customerStatement}
            />

            {isEditMode && (
                <>
                    <div className="my-3 border-top-1 border-300" />

                    <WarrantyClaimsDetailTabs id={id} dealerId={formData.bosBranchCode} dealerAddressId={formData.bosBranchSiteCode} warrType={data?.warrType} bulletNo={data?.bulletNo} />
                </>
            )}

            <StatusChangeModal
                visible={statusChangeModalVisible}
                onHide={() => setStatusChangeModalVisible(false)}
                onConfirm={handleStatusChangeConfirm}
                currentStatus={data?.status}
                currentStatusName={localizedCurrentStatusName}
                claimNo={data?.warrClaimsNo || ''}
                isLoading={isChangingStatus}
            />
        </div>
    );
};

export default WarrantyClaimsCreateEdit;
